import { InfiniteData } from "@tanstack/query-core";
import { Query, useQueryClient } from "@tanstack/react-query";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import { Id, Match, PaginatedCollection, ShortlistMinimized } from "PFTypes";
import { useCallback } from "react";

import { matchesKeys } from "./query_keys";

type MatchesQuery = Query<PaginatedCollection<Match[]> | InfiniteData<PaginatedCollection<Match[]>>>;

export const useMatchesShortlistReplace = () => {
  const queryClient = useQueryClient();

  const getMatchesQueries = useCallback(
    (activityId: Id) => {
      const matchesQueryKey = matchesKeys.activity(activityId);
      return queryClient.getQueryCache().findAll({
        predicate: (query) => isEqual(query.queryKey.slice(0, matchesQueryKey.length), matchesQueryKey)
      }) as MatchesQuery[];
    },
    [queryClient]
  );

  const replaceInCache = useCallback((matchesQueries: MatchesQuery[], mapEntry: (match: Match) => Match) => {
    matchesQueries.forEach((query) => {
      const changedQueryData = cloneDeep(query.state.data);
      if (!changedQueryData) {
        return;
      }
      if ("entries" in changedQueryData) {
        changedQueryData.entries = (changedQueryData.entries as Match[]).map<Match>(mapEntry);
        query.setData(changedQueryData);
      }
      if ("pages" in changedQueryData) {
        changedQueryData.pages = changedQueryData.pages.map((page) => {
          page.entries = (page.entries as Match[]).map<Match>(mapEntry);
          return page;
        });
        query.setData(changedQueryData);
      }
    });
  }, []);

  const replaceMatchesShortlistInCache = useCallback(
    (shortlist: ShortlistMinimized) => {
      const { profileId, activityId } = shortlist;
      const shortlistReplacement = pick(shortlist, "id", "state");

      const matchesQueries = getMatchesQueries(activityId);

      const mapEntry = (match: Match) => {
        if (match.id === profileId) {
          return {
            ...match,
            shortlist: shortlistReplacement
          };
        }
        return match;
      };

      replaceInCache(matchesQueries, mapEntry);
    },
    [getMatchesQueries, replaceInCache]
  );

  const deleteMatchesShortlistInCache = useCallback(
    (activityId: Id, shortlistId: Id) => {
      const matchesQueries = getMatchesQueries(activityId);

      const mapEntry = (match: Match) => {
        if (match.shortlist?.id === shortlistId) {
          return {
            ...match,
            shortlist: null
          };
        }

        return match;
      };
      replaceInCache(matchesQueries, mapEntry);
    },
    [getMatchesQueries, replaceInCache]
  );

  return {
    replaceMatchesShortlistInCache,
    deleteMatchesShortlistInCache
  };
};
